import { useState, type FormEvent } from 'react'
import { useAdminProfiles } from '@/hooks/useAdminDirectory'
import { useAdminInvitations } from '@/hooks/useAdminInvitations'
import { sectionStyle, sectionTitleStyle, rowStyle, pageTitleStyle, pageSubtitleStyle, statusPillStyle } from './adminListStyles'

// Superadmin-only — the route itself is guarded in App.tsx/AppShell.tsx by
// profile.is_superadmin, but this page also re-checks (defense in depth, same spirit as
// every RPC/Edge Function in this project never trusting a single layer) by simply
// rendering nothing useful if the invitations fetch comes back empty due to RLS, since a
// non-superadmin's admin_invitations SELECT policy denies every row.
export default function AdminAdmins() {
  const { profiles, loading: profilesLoading, updating, setAccountStatus, setSuperadminTier } = useAdminProfiles()
  const { invitations, loading: invitationsLoading, working, inviteAdmin, revokeInvitation } = useAdminInvitations()

  const admins = profiles.filter(p => p.primaryRole === 'drivetrack_admin')
  const pendingInvitations = invitations.filter(i => i.status === 'pending')

  const [email, setEmail] = useState('')
  const [grantSuperadmin, setGrantSuperadmin] = useState(false)
  const [formError, setFormError] = useState('')
  const [formNotice, setFormNotice] = useState('')

  const handleInvite = async (e: FormEvent) => {
    e.preventDefault()
    setFormError('')
    setFormNotice('')
    if (!email.trim()) { setFormError('Email is required.'); return }

    const result = await inviteAdmin(email.trim(), grantSuperadmin)
    if (!result.ok) { setFormError(result.error ?? 'Could not send invitation.'); return }

    setEmail('')
    setGrantSuperadmin(false)
    setFormNotice(
      result.emailed
        ? 'Invitation sent.'
        : `Invitation created, but the email failed to send. Share this link manually: ${window.location.origin}/accept-admin-invite#token=${result.token}`
    )
  }

  return (
    <div style={{ maxWidth: 900 }}>
      <div style={pageTitleStyle}>Admins</div>
      <div style={pageSubtitleStyle}>
        Platform administrators and pending invitations. Only a superadmin can see or use this page.
      </div>

      <div style={sectionStyle}>
        <div style={sectionTitleStyle}>Invite an admin</div>
        <form onSubmit={handleInvite} style={{ display: 'flex', gap: 10, alignItems: 'flex-end', flexWrap: 'wrap' }}>
          <div style={{ flex: '1 1 260px' }}>
            <label className='field-label'>Email</label>
            <input
              className='field-input'
              type='email'
              value={email}
              onChange={e => setEmail(e.target.value)}
              placeholder='colleague@example.com'
            />
          </div>
          <label style={{ display: 'flex', alignItems: 'center', gap: 7, font: '500 13px Inter,sans-serif', color: '#1B2A4A', paddingBottom: 10 }}>
            <input type='checkbox' checked={grantSuperadmin} onChange={e => setGrantSuperadmin(e.target.checked)} />
            Grant superadmin
          </label>
          <button type='submit' className='btn-primary' disabled={working} style={{ width: 'auto', padding: '10px 18px' }}>
            {working ? 'Sending…' : 'Send invite'}
          </button>
        </form>
        {formError && <div className='banner-error' style={{ marginTop: 10 }}>{formError}</div>}
        {formNotice && <div className='banner-info' style={{ marginTop: 10 }}>{formNotice}</div>}
      </div>

      <div style={sectionStyle}>
        <div style={sectionTitleStyle}>Pending invitations ({pendingInvitations.length})</div>
        {invitationsLoading ? <div style={{ color: 'rgba(27,42,74,0.4)' }}>Loading…</div> : pendingInvitations.length === 0 ? (
          <div style={{ color: 'rgba(27,42,74,0.4)' }}>None pending.</div>
        ) : pendingInvitations.map(inv => (
          <div key={inv.id} style={rowStyle}>
            <div style={{ flex: '1 1 220px', font: '600 13.5px Inter, sans-serif', color: '#1B2A4A' }}>{inv.invitedEmail}</div>
            <div style={{ flex: '1 1 160px', font: '400 12.5px Inter, sans-serif', color: 'rgba(27,42,74,0.55)' }}>
              {inv.grantSuperadmin ? 'Superadmin' : 'Admin'} · expires {new Date(inv.expiresAt).toLocaleDateString()}
            </div>
            <button
              onClick={() => revokeInvitation(inv.id)}
              disabled={working}
              style={{
                padding: '5px 12px', borderRadius: 7, border: '1px solid rgba(224,102,59,0.35)',
                background: 'transparent', font: '500 12px Inter, sans-serif', color: '#C0522A', cursor: 'pointer',
              }}
            >
              Revoke
            </button>
          </div>
        ))}
      </div>

      <div style={sectionStyle}>
        <div style={sectionTitleStyle}>Admins ({admins.length})</div>
        {profilesLoading ? <div style={{ color: 'rgba(27,42,74,0.4)' }}>Loading…</div> : admins.length === 0 ? (
          <div style={{ color: 'rgba(27,42,74,0.4)' }}>None yet.</div>
        ) : admins.map(a => (
          <div key={a.id} style={rowStyle}>
            <div style={{ flex: '1 1 200px', font: '600 13.5px Inter, sans-serif', color: '#1B2A4A' }}>
              {a.fullName ?? '(no name set)'}
            </div>
            <div style={{ flex: '1 1 220px', font: '400 12.5px Inter, sans-serif', color: 'rgba(27,42,74,0.55)' }}>
              {a.email}
            </div>
            <div style={statusPillStyle(a.accountStatus)}>{a.accountStatus}</div>
            {a.isSuperadmin && (
              <div style={{ padding: '3px 10px', borderRadius: 20, background: 'rgba(212,151,43,0.14)', color: '#A3701E', font: '600 11px Inter, sans-serif' }}>
                Superadmin
              </div>
            )}
            <button
              onClick={() => setSuperadminTier(a.id, !a.isSuperadmin)}
              disabled={updating === a.id}
              style={{
                padding: '5px 12px', borderRadius: 7, border: '1px solid rgba(27,42,74,0.15)',
                background: 'transparent', font: '500 12px Inter, sans-serif', color: '#1B2A4A', cursor: 'pointer',
              }}
            >
              {updating === a.id ? '…' : a.isSuperadmin ? 'Demote to admin' : 'Promote to superadmin'}
            </button>
            <button
              // "Delete" an admin, per this project's established "revoke, never hard-delete
              // an operational record" pattern (migration 015 also permanently blocks
              // clearing primary_role for every caller, so this can never be a role clear):
              // suspend the account. Reversible — a superadmin can reactivate later.
              // Superadmin tier is a separate, explicit action (the button above), not
              // folded into this one, so suspending never silently changes someone's tier.
              onClick={() => setAccountStatus(a.id, a.accountStatus === 'active' ? 'suspended' : 'active')}
              disabled={updating === a.id}
              style={{
                padding: '5px 12px', borderRadius: 7, border: '1px solid rgba(224,102,59,0.35)',
                background: 'transparent', font: '500 12px Inter, sans-serif', color: '#C0522A', cursor: 'pointer',
              }}
            >
              {a.accountStatus === 'active' ? 'Revoke access' : 'Restore access'}
            </button>
          </div>
        ))}
      </div>
    </div>
  )
}
